import React from 'react';
import BRAND from '../config/brand';
import { openCookiePreferences } from '../lib/consent';
import './PrivacyPolicyPage.css';

const updatedDate = 'November 3, 2026';

function PrivacyPolicyPage() {
  React.useEffect(() => {
    const id = window.location.hash.replace(/^#/, '');
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <main className="privacy-page">
      <header className="privacy-header">
        <a className="privacy-logo-link" href="/" aria-label="Bautista Planning and Analytics home">
          <img
            src={BRAND.logoSrc}
            alt="Bautista Planning and Analytics"
            className="privacy-logo"
            width={300}
            height={60}
            decoding="async"
          />
        </a>
        <p className="privacy-kicker">Privacy Policy</p>
        <h1>Bautista Planning and Analytics, LLC</h1>
        <p className="privacy-date">Effective and last updated: {updatedDate}</p>
      </header>

      <section className="privacy-section privacy-intro">
        <p>
          Bautista Planning and Analytics, LLC ("BPA, LLC") recognizes that relationships with current
          and prospective clients are based on integrity and trust. We work to maintain your privacy and
          preserve the private nature of our relationship with you.
        </p>
        <p>
          BPA, LLC will not sell your personal information. We will not disclose personal information
          unless permitted or required by law, needed to provide services, or directed by you.
          BPA, LLC provides this Privacy Policy to clients annually.
        </p>
      </section>

      <section className="privacy-section">
        <h2>Why We Collect Information</h2>
        <ul>
          <li>To design, implement, and service financial planning and investment-related services.</li>
          <li>To maintain client records and communicate with clients and prospective clients.</li>
          <li>To comply with federal and state laws, regulations, supervisory obligations, and records requirements.</li>
        </ul>
      </section>

      <section className="privacy-section">
        <h2>Information We Collect and Maintain</h2>
        <p>We may collect nonpublic personal information, including:</p>
        <ul>
          <li>Identity and contact information such as name, address, email, phone number, date of birth, and tax identifiers.</li>
          <li>Financial information shared in consultations, planning documents, statements, forms, or secure client portals.</li>
          <li>Information generated while providing planning, reporting, investment, tax, or cash-flow analysis.</li>
          <li>Information received from custodians, account aggregation providers, payroll records, tax documents, or other authorized third parties.</li>
        </ul>
      </section>

      <section className="privacy-section">
        <h2>Information We Disclose</h2>
        <p>
          We may disclose nonpublic personal information to unaffiliated third parties in limited
          circumstances, including custodians, technology providers, account aggregation providers,
          compliance consultants, independent contractors, or other service providers that help us provide
          advisory and planning services.
        </p>
        <p>Otherwise, BPA, LLC will not disclose personal information unless:</p>
        <ul>
          <li>We receive your prior consent or direction.</li>
          <li>The recipient is your authorized representative.</li>
          <li>Disclosure is required or permitted by law, regulation, legal process, or examination.</li>
          <li>The disclosure is necessary to service your relationship with BPA, LLC.</li>
        </ul>
        <p>
          Arrangements with unaffiliated companies or independent contractors are subject to confidentiality
          expectations and appropriate vendor oversight.
        </p>
      </section>

      <section className="privacy-section">
        <h2>Advisor Transition Opt-Out</h2>
        <p>
          If a Financial Planner or Financial Adviser leaves BPA, LLC to join another firm, that person may
          be permitted to retain copies of client information so they can assist with account transfer and
          continuity of service, where permitted by law and applicable agreements.
        </p>
        <p>
          If you do not want your Financial Planner or Financial Adviser to retain copies of your client
          sensitive information when leaving BPA, LLC, contact us at{' '}
          <a href="mailto:joe@bautistaplanningandanalytics.com">joe@bautistaplanningandanalytics.com</a>.
        </p>
      </section>

      <section className="privacy-section">
        <h2>How We Protect Information</h2>
        <p>
          We restrict access to client information to people and systems that need it to perform business,
          service, compliance, or technology functions. We maintain physical, electronic, and procedural
          safeguards intended to protect confidential personal information.
        </p>
        <ul>
          <li>Access to client records is limited by business need.</li>
          <li>Employees and contractors are expected to safeguard client information.</li>
          <li>We use secure systems and vendor controls for account aggregation, file storage, authentication, and reporting where appropriate.</li>
          <li>We maintain policies and procedures for safeguarding information, disposal, and responding to unauthorized access or use of customer information.</li>
        </ul>
      </section>

      <section className="privacy-section">
        <h2>Use of Planning Technology and AI Tools</h2>
        <p>
          BPA, LLC may use software tools to organize documents, prepare reports, analyze planning scenarios,
          and improve internal workflows. Client information should be limited to what is needed for the
          specific advisory task, and sensitive identifiers should be redacted or minimized when practical.
        </p>
        <p>
          Publicly available information on social media or other websites is still treated carefully when it
          is combined with advisory records, financial information, dates of birth, contact information, or
          other client-specific planning data.
        </p>
      </section>

      <section className="privacy-section" id="cookies">
        <h2>Website, Cookies, and Analytics</h2>
        <p>
          When you visit this website, we and our service providers may use cookies and similar browser
          storage. We group them into two categories:
        </p>
        <ul>
          <li>
            <strong>Essential.</strong> Needed for the site and client portal to work: remembering your
            cookie choice, keeping you signed in, completing bank connections, and keeping track of how you
            arrived on the site during your visit so a contact request reaches the right place. These end
            when you close the tab or sign out, except your cookie choice, which we keep for up to 12 months.
          </li>
          <li>
            <strong>Cookieless measurement (until you choose).</strong> Before you make a cookie choice, we
            use Google Analytics 4 in a cookieless mode to count page visits, calculator use and booking
            clicks. In this mode no analytics cookies are set and no visitor identifier is stored in your
            browser; Google receives the page, the event and the standard technical details every web
            request carries, and uses them to estimate overall visit counts. Choosing &ldquo;Decline
            analytics&rdquo;, or browsing with Global Privacy Control turned on, turns this off.
          </li>
          <li>
            <strong>Analytics (only with your permission).</strong> If you allow analytics, we use Google
            Analytics 4 to count page visits and calculator use, and we store a random visit identifier and
            the campaign link you first arrived from. We send only general categories (for example, which
            calculator was used or which page you came from), not the numbers you type into calculators,
            your form answers, or your client data. Google signals and advertising features are turned off.
          </li>
        </ul>
        <p>
          We do not use advertising cookies, and we do not sell or share personal information for
          cross-context behavioral advertising.
        </p>
        <h3>Your Choices</h3>
        <ul>
          <li>
            You can allow or decline analytics at any time from{' '}
            <button type="button" className="privacy-inline-button" onClick={openCookiePreferences}>
              Cookie preferences
            </button>
            , also linked in the site footer. Declining removes the analytics identifiers stored by this site.
          </li>
          <li>
            If your browser sends a Global Privacy Control signal, we treat it as a decline unless you turn
            analytics on yourself.
          </li>
          <li>
            You can also block or delete cookies in your browser settings, or use Google's{' '}
            <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
              Analytics opt-out add-on
            </a>
            .
          </li>
        </ul>
      </section>

      <section className="privacy-section">
        <h2>Third-Party Services</h2>
        <p>We use these service providers to run the site and client portal:</p>
        <ul>
          <li>
            <strong>Plaid</strong> connects your financial accounts when you choose to link them. Plaid's
            handling of your data is described in the{' '}
            <a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer">
              Plaid End User Privacy Policy
            </a>
            .
          </li>
          <li><strong>Supabase</strong> provides secure sign-in and data storage for the client portal.</li>
          <li>
            <strong>Google Analytics</strong> provides site analytics: cookieless counts until you make a
            cookie choice, and full analytics only if you allow it.
          </li>
          <li><strong>TidyCal</strong> handles meeting scheduling when you book a call.</li>
        </ul>
      </section>

      <section className="privacy-section">
        <h2>Text Messages</h2>
        <p>
          If you opt in to text messages, we use your mobile number only to send the messages you asked for.
          Message frequency varies, and message and data rates may apply. Reply STOP to opt out or HELP for
          help. We do not share or sell mobile numbers or text message opt-in information with third parties
          or affiliates for their marketing or promotional purposes.
        </p>
      </section>

      <section className="privacy-section">
        <h2>Children</h2>
        <p>
          This site is not directed to children under 13, and we do not knowingly collect personal
          information from them.
        </p>
      </section>

      <section className="privacy-section">
        <h2>Former Clients</h2>
        <p>
          We protect information about former clients under the same general standards that apply to current
          clients, subject to legal, regulatory, and records-retention requirements.
        </p>
      </section>

      <section className="privacy-section">
        <h2>Questions</h2>
        <p>
          Contact BPA, LLC at{' '}
          <a href="mailto:joe@bautistaplanningandanalytics.com">joe@bautistaplanningandanalytics.com</a> with
          questions about this Privacy Policy or your information, or to request access to or correction
          of your information.
        </p>
        <p>
          We may update this Privacy Policy and will post the new version on this page with a new date.
        </p>
      </section>
    </main>
  );
}

export default PrivacyPolicyPage;
